import clsx from "clsx";
import type { ReactNode } from "react";
import { CheckIcon } from "@/components/ui/Icons";

export type OptionTileItem = {
  id: string;
  title: string;
  description?: string | null;
  meta?: ReactNode;
};

type Props = {
  title: string;
  hint?: string;
  items: OptionTileItem[];
  selectedIds: Set<string>;
  onToggle: (id: string, selected: boolean) => void;
  columns?: 1 | 2;
};

/** Multi-select list rendered as tappable tiles (replaces plain checkbox lists). */
export function OptionTileGroup({
  title,
  hint,
  items,
  selectedIds,
  onToggle,
  columns = 2,
}: Props) {
  if (items.length === 0) return null;
  return (
    <fieldset className="bk-section">
      <legend className="bk-section-title">{title}</legend>
      {hint ? <p className="bk-section-hint">{hint}</p> : null}
      <div
        className={clsx(
          "grid gap-2",
          columns === 2 && "sm:grid-cols-2",
        )}
      >
        {items.map((item) => {
          const selected = selectedIds.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              role="checkbox"
              aria-checked={selected}
              onClick={() => onToggle(item.id, !selected)}
              className={clsx("bk-tile bk-tile--compact", selected && "bk-tile--selected")}
            >
              <span className="bk-tile__check bk-tile__check--square">
                <CheckIcon />
              </span>
              <span className="bk-tile__body">
                <span className="bk-tile__title">{item.title}</span>
                {item.description ? (
                  <span className="bk-tile__hint">{item.description}</span>
                ) : null}
              </span>
              {item.meta ? <span className="bk-tile__meta">{item.meta}</span> : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
